import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { scheduleService, participantsService, groupsService } from "../../services";
import resultsService from "../../services/resultsService";
import resultsStreamService from "../../services/resultsStreamService";

const JUDGE_INDICES = [0, 1, 2, 3, 4];

const LiveResultsView = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const venue = searchParams.get("venue") || "Fläche A";

  const [loading, setLoading] = useState(true);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [partnerTeams, setPartnerTeams] = useState([]);
  const [isPartnerForm, setIsPartnerForm] = useState(false);
  const [results, setResults] = useState(new Map());
  const [lastUpdate, setLastUpdate] = useState(null);
  const [updatedValues, setUpdatedValues] = useState(new Set());
  const previousPositionsRef = useRef(new Map());

  useEffect(() => {
    loadInitialData();
  }, [venue]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await loadEventAndParticipants(true);
    } catch (error) {
      console.error("Fehler beim Laden der Daten:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadEventAndParticipants = async (loadInitialResults = false) => {
    const scheduleResult = await scheduleService.getAllScheduleEvents({ venue });
    const scheduleEvents = scheduleResult.success ? scheduleResult.data || [] : [];

    scheduleEvents.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    const activeEvent = scheduleEvents.find((e) => e.status === "Aktiv");

    setCurrentEvent((prevEvent) => {
      const currentGroupId = prevEvent?.group?.id;
      const newGroupId = activeEvent?.group?.id;

      if (currentGroupId !== newGroupId) {
        if (activeEvent && activeEvent.group) {
          const isPartnerFormGroup = activeEvent.group.category?.type === "Partnerform";
          setIsPartnerForm(isPartnerFormGroup);

          if (isPartnerFormGroup) {
            groupsService.getGroupWithStartOrder(activeEvent.group.id).then((groupResponse) => {
              if (groupResponse.success && groupResponse.data?.partnerTeams) {
                const teams = groupResponse.data.partnerTeams.sort((a, b) => {
                  const aStartNumber = a.startNumber || 999;
                  const bStartNumber = b.startNumber || 999;
                  return aStartNumber - bStartNumber;
                });
                setPartnerTeams(teams);
                setParticipants([]);

                if (loadInitialResults) {
                  loadResults(activeEvent.group.id);
                } else {
                  setResults(new Map());
                }
              } else {
                setPartnerTeams([]);
                setParticipants([]);
                setResults(new Map());
              }
            });
          } else {
            participantsService.getAllParticipants().then((participantsResult) => {
              if (participantsResult.success) {
                const allParticipants = participantsResult.data || [];
                const eventParticipants = allParticipants.filter((p) => {
                  if (!p.groups || !Array.isArray(p.groups)) return false;
                  return p.groups.some((g) => g.id === activeEvent.group.id);
                });

                eventParticipants.sort((a, b) => {
                  const aGroup = a.groups.find((g) => g.id === activeEvent.group.id);
                  const bGroup = b.groups.find((g) => g.id === activeEvent.group.id);
                  const aStartNumber = aGroup ? aGroup.startNumber : 999;
                  const bStartNumber = bGroup ? bGroup.startNumber : 999;
                  return aStartNumber - bStartNumber;
                });

                setParticipants(eventParticipants);
                setPartnerTeams([]);

                if (loadInitialResults) {
                  loadResults(activeEvent.group.id);
                } else {
                  setResults(new Map());
                }
              }
            });
          }
        } else {
          setParticipants([]);
          setPartnerTeams([]);
          setIsPartnerForm(false);
          setResults(new Map());
        }
      }

      return activeEvent || null;
    });

    if (loadInitialResults && activeEvent?.group?.id) {
      await loadResults(activeEvent.group.id);
    }
  };

  const loadResults = async (groupId) => {
    if (!groupId) return;

    try {
      const resultsResult = await resultsService.getResultsByGroup(groupId);
      if (resultsResult.success && resultsResult.data && Array.isArray(resultsResult.data)) {
        const existingResults = resultsResult.data;
        const resultsMap = new Map();

        existingResults.forEach((result) => {
          const identifier = result.partnerTeamId
            ? `partner-team-${result.partnerTeamId}`
            : result.participantId
              ? result.participantId
              : result.nationId
                ? `nation-${result.nationId}`
                : null;

          if (identifier) {
            const judgeScores = [
              parseFloat(result.judgeScore1) || 0,
              parseFloat(result.judgeScore2) || 0,
              parseFloat(result.judgeScore3) || 0,
              parseFloat(result.judgeScore4) || 0,
              parseFloat(result.judgeScore5) || 0,
            ];
            const penalty = parseFloat(result.penalty) || 0;
            const validScores = judgeScores.filter((score) => score > 0);
            const averageScore =
              validScores.length > 0 ? validScores.reduce((sum, s) => sum + s, 0) / validScores.length : 0;
            const finalScore = averageScore + penalty;

            resultsMap.set(identifier, {
              judgeScores,
              penalty,
              finalScore: finalScore.toFixed(2),
              rank: result.rank || null,
              didNotStart: result.didNotStart || false,
              isLate: result.isLate || false,
            });
          }
        });

        setResults(resultsMap);
      }
    } catch (error) {
      console.warn("Fehler beim Laden der Ergebnisse:", error);
    }
  };

  useEffect(() => {
    loadEventAndParticipants();

    const interval = setInterval(async () => {
      await loadEventAndParticipants();
    }, 30000);

    return () => clearInterval(interval);
  }, [venue]);

  useEffect(() => {
    const groupId = currentEvent?.group?.id;

    if (!groupId) {
      resultsStreamService.disconnect();
      return;
    }

    const handleUpdate = (update) => {
      if (update.type === "error") {
        return;
      }

      if (update.type === "connected") {
        return;
      }

      if (update.type === "result-update") {
        const result = update.result;
        const identifier = result.partnerTeamId
          ? `partner-team-${result.partnerTeamId}`
          : result.participantId
            ? result.participantId
            : result.nationId
              ? `nation-${result.nationId}`
              : null;

        if (identifier) {
          const judgeScores = [
            parseFloat(result.judgeScore1) || 0,
            parseFloat(result.judgeScore2) || 0,
            parseFloat(result.judgeScore3) || 0,
            parseFloat(result.judgeScore4) || 0,
            parseFloat(result.judgeScore5) || 0,
          ];
          const penalty = parseFloat(result.penalty) || 0;
          const validScores = judgeScores.filter((score) => score > 0);
          const averageScore =
            validScores.length > 0 ? validScores.reduce((sum, s) => sum + s, 0) / validScores.length : 0;
          const finalScore = averageScore + penalty;

          setResults((prev) => {
            const newMap = new Map(prev);
            const oldResult = prev.get(identifier);

            const oldJudgeScores =
              oldResult?.judgeScores && Array.isArray(oldResult.judgeScores)
                ? oldResult.judgeScores
                : [null, null, null, null, null];

            const changedKeys = new Set();
            if (!oldResult || oldResult.finalScore !== finalScore.toFixed(2)) {
              changedKeys.add(`finalScore-${identifier}`);
            }
            judgeScores.forEach((score, idx) => {
              const oldScore = oldJudgeScores[idx];
              if (!oldResult || oldScore === undefined || oldScore === null || oldScore !== score) {
                changedKeys.add(`judgeScore${idx + 1}-${identifier}`);
              }
            });
            if (!oldResult || oldResult.penalty !== penalty) {
              changedKeys.add(`penalty-${identifier}`);
            }
            if (!oldResult || oldResult.rank !== (result.rank || null)) {
              changedKeys.add(`rank-${identifier}`);
            }

            if (changedKeys.size > 0) {
              setUpdatedValues((prevSet) => {
                const newSet = new Set(prevSet);
                changedKeys.forEach((key) => newSet.add(key));
                setTimeout(() => {
                  setUpdatedValues((current) => {
                    const updated = new Set(current);
                    changedKeys.forEach((key) => updated.delete(key));
                    return updated;
                  });
                }, 500);
                return newSet;
              });
            }

            newMap.set(identifier, {
              judgeScores,
              penalty,
              finalScore: finalScore.toFixed(2),
              rank: result.rank || null,
              didNotStart: result.didNotStart || false,
              isLate: result.isLate || false,
            });
            return newMap;
          });

          setLastUpdate(new Date());
        }
      } else if (update.type === "rank-update") {
        setResults((prev) => {
          const newMap = new Map(prev);

          update.results.forEach((result) => {
            const identifier = result.partnerTeamId
              ? `partner-team-${result.partnerTeamId}`
              : result.participantId
                ? result.participantId
                : result.nationId
                  ? `nation-${result.nationId}`
                  : null;

            if (identifier) {
              const existing = newMap.get(identifier) || {};
              const oldRank = existing.rank;
              const newRank = result.rank || null;

              if (oldRank !== newRank) {
                setUpdatedValues((prevSet) => {
                  const newSet = new Set(prevSet);
                  newSet.add(`rank-${identifier}`);
                  setTimeout(() => {
                    setUpdatedValues((current) => {
                      const updated = new Set(current);
                      updated.delete(`rank-${identifier}`);
                      return updated;
                    });
                  }, 500);
                  return newSet;
                });
              }

              newMap.set(identifier, {
                ...existing,
                rank: newRank,
              });
            }
          });

          return newMap;
        });

        setLastUpdate(new Date());
      }
    };

    resultsStreamService.connect(groupId, handleUpdate);

    return () => {
      resultsStreamService.disconnectGroup(groupId);
    };
  }, [currentEvent?.group?.id]);

  const formatTime = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString(i18n.language === "zh" ? "zh-CN" : i18n.language === "en" ? "en-GB" : "de-DE", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [i18n.language]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "zh" ? "zh-CN" : i18n.language === "en" ? "en-GB" : "de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }, [i18n.language]);

  const sortedItems = useMemo(() => {
    const itemsToSort = isPartnerForm ? partnerTeams : participants;

    const sorted = [...itemsToSort].sort((a, b) => {
      const identifierA = isPartnerForm ? `partner-team-${a.id}` : a.id;
      const identifierB = isPartnerForm ? `partner-team-${b.id}` : b.id;

      const aResult = results.get(identifierA);
      const bResult = results.get(identifierB);
      const aDidNotStart = aResult?.didNotStart || false;
      const bDidNotStart = bResult?.didNotStart || false;

      if (aDidNotStart && !bDidNotStart) return 1;
      if (!aDidNotStart && bDidNotStart) return -1;

      const aScore = aResult ? parseFloat(aResult.finalScore) : -1;
      const bScore = bResult ? parseFloat(bResult.finalScore) : -1;

      if (aScore !== bScore) {
        return bScore - aScore;
      }

      if (isPartnerForm) {
        const aStartNumber = a.startNumber || 999;
        const bStartNumber = b.startNumber || 999;
        return aStartNumber - bStartNumber;
      }
      const aGroup = a.groups.find((g) => g.id === currentEvent?.group?.id);
      const bGroup = b.groups.find((g) => g.id === currentEvent?.group?.id);
      const aStartNumber = aGroup ? aGroup.startNumber : 999;
      const bStartNumber = bGroup ? bGroup.startNumber : 999;
      return aStartNumber - bStartNumber;
    });

    const currentPositions = new Map();
    sorted.forEach((item, index) => {
      const identifier = isPartnerForm ? `partner-team-${item.id}` : item.id;
      currentPositions.set(identifier, index);
    });

    const prevPositions = previousPositionsRef.current;
    const hasPositionChanges = Array.from(currentPositions.entries()).some(([id, pos]) => {
      return prevPositions.get(id) !== undefined && prevPositions.get(id) !== pos;
    });

    if (hasPositionChanges) {
      setTimeout(() => {
        previousPositionsRef.current = currentPositions;
      }, 50);
    } else {
      previousPositionsRef.current = currentPositions;
    }

    return sorted;
  }, [isPartnerForm, partnerTeams, participants, results, currentEvent?.group?.id]);

  const getPositionChange = (identifier) => {
    const prevPos = previousPositionsRef.current.get(identifier);
    const currentPos = sortedItems.findIndex((item) => {
      const itemIdentifier = isPartnerForm ? `partner-team-${item.id}` : item.id;
      return itemIdentifier === identifier;
    });

    if (prevPos === undefined || prevPos === currentPos) {
      return null;
    }

    return {
      from: prevPos,
      to: currentPos,
      direction: currentPos < prevPos ? "up" : "down",
    };
  };

  const renderJudgeCell = (judgeIndex, didNotStart, judgeScores, identifier) => {
    if (didNotStart) {
      return <span className="text-sm text-slate-400 dark:text-slate-500 tabular-nums">—</span>;
    }
    const score = judgeScores[judgeIndex];
    if (score === null || score === undefined) {
      return <span className="text-sm text-slate-400 dark:text-slate-500">—</span>;
    }
    const flash = updatedValues.has(`judgeScore${judgeIndex + 1}-${identifier}`);
    return (
      <div className="flex justify-center">
        <span
          className={`inline-flex min-w-[3.25rem] justify-center rounded-lg border px-2 py-1 text-xs font-semibold tabular-nums transition-all duration-300 ${
            flash
              ? "border-emerald-400 bg-emerald-50 text-emerald-900 dark:border-emerald-500 dark:bg-emerald-900/35 dark:text-emerald-100"
              : "border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-600 dark:bg-slate-700/90 dark:text-slate-100"
          }`}
        >
          {Number(score).toFixed(2)}
        </span>
      </div>
    );
  };

  const rankRowClass = (actualRank, didNotStart) => {
    if (didNotStart) return "opacity-60 grayscale-[0.35]";
    if (actualRank === 1) return "bg-amber-50/90 dark:bg-amber-950/25";
    if (actualRank === 2) return "bg-slate-100/80 dark:bg-slate-700/35";
    if (actualRank === 3) return "bg-orange-50/80 dark:bg-orange-950/20";
    return "bg-white dark:bg-slate-800/50";
  };

  const rankBadgeClass = (actualRank, didNotStart) => {
    if (didNotStart) {
      return "bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800";
    }
    if (actualRank === 1) {
      return "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100 dark:border-amber-700";
    }
    if (actualRank === 2) {
      return "bg-slate-200 text-slate-800 border-slate-300 dark:bg-slate-600 dark:text-slate-100 dark:border-slate-500";
    }
    if (actualRank === 3) {
      return "bg-orange-100 text-orange-900 border-orange-200 dark:bg-orange-900/35 dark:text-orange-100 dark:border-orange-800";
    }
    return "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600";
  };

  if (loading) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-slate-50/50 dark:bg-slate-900 flex items-center justify-center px-4 transition-colors">
        <div className="text-center">
          <div
            className="mx-auto mb-4 h-12 w-12 rounded-full border-[3px] border-primary border-t-transparent dark:border-primary-light animate-spin"
            aria-hidden
          />
          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{t("liveResults.loading")}</p>
        </div>
      </div>
    );
  }

  if (!currentEvent) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-slate-50/50 dark:bg-slate-900 flex items-center justify-center px-4 transition-colors">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-8 text-center shadow-sm">
          <div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-700"
            aria-hidden
          >
            <i className="fas fa-calendar-times text-xl text-slate-400 dark:text-slate-500" />
          </div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {t("liveResults.noActiveEvent")}
          </h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
            {t("liveResults.noActiveEventVenue", { venue })}
          </p>
        </div>
      </div>
    );
  }

  const groupTitle = currentEvent.group?.name || currentEvent.category;
  const scheduleLine = `${formatDate(currentEvent.startTime)} · ${formatTime(currentEvent.startTime)} – ${formatTime(currentEvent.endTime)}`;

  return (
    <div className="min-h-screen min-h-[100dvh] bg-slate-50/50 dark:bg-slate-900 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Kopfbereich */}
        <header className="mb-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 sm:p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">
                {t("liveResults.venueLabel")}: {venue}
              </p>
              <h1
                id="live-results-heading"
                className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 truncate"
              >
                {groupTitle}
              </h1>
              <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
                <span className="inline-flex items-center gap-1.5">
                  <i className="fas fa-calendar-day text-[11px] text-slate-400" aria-hidden />
                  {scheduleLine}
                </span>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 lg:justify-end">
              <div
                className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
                role="status"
                aria-label={t("liveResults.live")}
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                {t("liveResults.live")}
              </div>
              {lastUpdate && (
                <div
                  className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/40 px-3 py-2 text-right"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {t("liveResults.lastUpdate")}
                  </p>
                  <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-slate-100">
                    <i className="fas fa-rotate text-xs mr-1.5 text-slate-400" aria-hidden />
                    {formatTime(lastUpdate.toISOString())}
                  </p>
                </div>
              )}
            </div>
          </div>
        </header>

        <p className="mb-2 text-center text-[11px] text-slate-500 dark:text-slate-400 sm:hidden">{t("liveResults.scrollHint")}</p>

        {sortedItems.length > 0 ? (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
            <div className="overflow-x-auto overscroll-x-contain">
              <table className="min-w-[920px] w-full border-collapse text-sm">
                <caption className="sr-only">{t("liveResults.tableCaption")}</caption>
                <thead className="border-b border-slate-200 dark:border-slate-700">
                  <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    <th
                      scope="col"
                      className="sticky top-0 z-10 whitespace-nowrap bg-slate-50 px-4 py-3 shadow-[inset_0_-1px_0_0_rgb(226_232_240)] dark:bg-slate-800 dark:shadow-[inset_0_-1px_0_0_rgb(51_65_85)] sm:px-5"
                    >
                      {t("liveResults.rank")}
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 whitespace-nowrap bg-slate-50 px-4 py-3 shadow-[inset_0_-1px_0_0_rgb(226_232_240)] dark:bg-slate-800 dark:shadow-[inset_0_-1px_0_0_rgb(51_65_85)] sm:px-5"
                    >
                      {t("liveResults.startNr")}
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 min-w-[200px] whitespace-nowrap bg-slate-50 px-4 py-3 shadow-[inset_0_-1px_0_0_rgb(226_232_240)] dark:bg-slate-800 dark:shadow-[inset_0_-1px_0_0_rgb(51_65_85)] sm:px-5"
                    >
                      {isPartnerForm ? t("liveResults.team") : t("liveResults.athlete")}
                    </th>
                    {JUDGE_INDICES.map((idx) => (
                      <th
                        key={idx}
                        scope="col"
                        className="sticky top-0 z-10 whitespace-nowrap bg-slate-50 px-3 py-3 text-center shadow-[inset_0_-1px_0_0_rgb(226_232_240)] dark:bg-slate-800 dark:shadow-[inset_0_-1px_0_0_rgb(51_65_85)]"
                      >
                        {t("liveResults.judgeRound", { round: idx + 1 })}
                      </th>
                    ))}
                    <th
                      scope="col"
                      className="sticky top-0 z-10 whitespace-nowrap bg-slate-50 px-3 py-3 text-center shadow-[inset_0_-1px_0_0_rgb(226_232_240)] dark:bg-slate-800 dark:shadow-[inset_0_-1px_0_0_rgb(51_65_85)]"
                    >
                      {t("liveResults.penalty")}
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 whitespace-nowrap bg-slate-50 px-4 py-3 text-right shadow-[inset_0_-1px_0_0_rgb(226_232_240)] dark:bg-slate-800 dark:shadow-[inset_0_-1px_0_0_rgb(51_65_85)] sm:pr-5"
                    >
                      {t("liveResults.finalScore")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {sortedItems.map((item, index) => {
                    const identifier = isPartnerForm ? `partner-team-${item.id}` : item.id;
                    const itemResult = results.get(identifier);
                    const judgeScores =
                      itemResult?.judgeScores && Array.isArray(itemResult.judgeScores)
                        ? itemResult.judgeScores
                        : [null, null, null, null, null];

                    let nationCode;
                    let name;
                    let nationName;
                    let startNumber;
                    if (isPartnerForm) {
                      nationCode = item.participant1?.nation?.code || "DE";
                      name = `${item.participant1?.name || "—"} & ${item.participant2?.name || "—"}`;
                      nationName = item.participant1?.nation?.name || "";
                      startNumber = item.startNumber || null;
                    } else {
                      nationCode = item.nation?.code || item.nationCode || "DE";
                      name = item.name;
                      nationName = item.nation?.name || item.nation || "";
                      const participantGroup = item.groups.find((g) => g.id === currentEvent.group.id);
                      startNumber = participantGroup ? participantGroup.startNumber : null;
                    }

                    const nationLabel =
                      typeof nationName === "string" ? nationName : nationName?.name || String(nationName || "");

                    const didNotStart = itemResult?.didNotStart || false;
                    const isLate = itemResult?.isLate || false;

                    const actualRank = didNotStart
                      ? null
                      : sortedItems.slice(0, index + 1).filter((i) => {
                          const iIdentifier = isPartnerForm ? `partner-team-${i.id}` : i.id;
                          const iResult = results.get(iIdentifier);
                          return !(iResult?.didNotStart || false);
                        }).length;

                    const positionChange = getPositionChange(identifier);
                    const animationClass =
                      positionChange !== null
                        ? positionChange.direction === "up"
                          ? "animate-live-row-up"
                          : "animate-live-row-down"
                        : "";

                    return (
                      <tr
                        key={item.id}
                        className={`transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-700/30 ${animationClass} ${rankRowClass(actualRank, didNotStart)}`}
                      >
                        <td className="whitespace-nowrap px-4 py-3 sm:px-5 align-middle">
                          {didNotStart ? (
                            <span
                              className={`inline-flex h-10 min-w-[2.5rem] items-center justify-center rounded-full border px-2 text-xs font-bold ${rankBadgeClass(null, true)}`}
                            >
                              {t("liveResults.dns")}
                            </span>
                          ) : (
                            <span
                              className={`inline-flex h-10 w-10 items-center justify-center rounded-full border text-base font-bold tabular-nums ${rankBadgeClass(actualRank, false)}`}
                            >
                              {actualRank}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 sm:px-5 align-middle">
                          <span className="inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold tabular-nums text-slate-800 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200">
                            {startNumber ?? "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 sm:px-5 align-middle">
                          <div className="flex items-center gap-3 min-w-0">
                            <img
                              src={`${process.env.PUBLIC_URL}/images/flags/${nationCode}.png`}
                              alt=""
                              className={`h-8 w-8 shrink-0 rounded-full border border-slate-200 object-cover dark:border-slate-600 ${didNotStart ? "opacity-50" : ""}`}
                            />
                            <div className="min-w-0">
                              <div
                                className={`flex flex-wrap items-center gap-2 font-medium ${didNotStart ? "text-slate-500 dark:text-slate-400" : "text-slate-900 dark:text-slate-100"}`}
                              >
                                <span className="truncate">{name}</span>
                                {didNotStart && (
                                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300">
                                    <i className="fas fa-times-circle text-[9px]" aria-hidden />
                                    {t("liveResults.dns")}
                                  </span>
                                )}
                                {isLate && !didNotStart && (
                                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
                                    <i className="fas fa-clock text-[9px]" aria-hidden />
                                    {t("liveResults.late")}
                                  </span>
                                )}
                              </div>
                              {nationLabel ? (
                                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 truncate uppercase tracking-wide">
                                  {nationLabel}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        {JUDGE_INDICES.map((idx) => (
                          <td key={idx} className="px-3 py-3 align-middle">
                            {renderJudgeCell(idx, didNotStart, judgeScores, identifier)}
                          </td>
                        ))}
                        <td className="px-3 py-3 align-middle text-center">
                          {didNotStart ? (
                            <span className="text-slate-400">—</span>
                          ) : itemResult?.penalty !== undefined &&
                            itemResult.penalty !== null &&
                            itemResult.penalty !== 0 ? (
                            (() => {
                              const penaltyValue = parseFloat(itemResult.penalty);
                              const formattedPenalty = Math.abs(penaltyValue).toFixed(2);
                              const isNegative = penaltyValue < 0;
                              const flash = updatedValues.has(`penalty-${identifier}`);
                              return (
                                <span
                                  className={`inline-flex min-w-[3rem] justify-center rounded-lg border px-2 py-1 text-xs font-semibold tabular-nums transition-all ${
                                    isNegative
                                      ? "border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200"
                                      : "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200"
                                  } ${flash ? "ring-2 ring-primary/30 dark:ring-primary-light/30" : ""}`}
                                >
                                  {isNegative ? "−" : "+"}
                                  {formattedPenalty}
                                </span>
                              );
                            })()
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right align-middle sm:pr-5">
                          {didNotStart ? (
                            <span className="text-sm font-semibold tabular-nums text-slate-400 dark:text-slate-500">
                              0.00
                            </span>
                          ) : itemResult?.finalScore !== undefined && itemResult.finalScore !== null ? (
                            <div className="flex justify-end">
                              <span
                                className={`inline-flex min-w-[4.5rem] justify-center rounded-xl border-2 px-3 py-2 text-lg font-bold tabular-nums transition-all duration-300 ${
                                  actualRank === 1
                                    ? "border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-500 dark:bg-amber-950/40 dark:text-amber-100"
                                    : actualRank === 2
                                      ? "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-500 dark:bg-slate-700 dark:text-slate-100"
                                      : actualRank === 3
                                        ? "border-orange-400 bg-orange-50 text-orange-900 dark:border-orange-600 dark:bg-orange-950/35 dark:text-orange-100"
                                        : "border-primary/30 bg-primary/5 text-primary dark:border-primary-light/40 dark:bg-primary-light/10 dark:text-primary-light"
                                } ${updatedValues.has(`finalScore-${identifier}`) ? "scale-[1.02] ring-2 ring-emerald-400/50 dark:ring-emerald-500/40" : ""}`}
                              >
                                {itemResult.finalScore}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm font-semibold text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-10 text-center shadow-sm">
            <div
              className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-700"
              aria-hidden
            >
              <i className="fas fa-users text-xl text-slate-400 dark:text-slate-500" />
            </div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">{t("liveResults.noParticipants")}</h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">{t("liveResults.noParticipantsHint")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveResultsView;
